import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState, type FormEvent } from 'react'
import { ApiError, errorMessages, type PostValue } from '../../api/client'
import {
  moduleAlarmCheckValue,
  moduleAlarmCreate,
  moduleAlarmEditForm,
  moduleAlarmListQuery,
  moduleAlarmLoadDefaults,
  moduleAlarmRemove,
  moduleAlarmStatus,
  moduleAlarmUpdate,
  type AlarmCategory,
  type AlarmRow,
  type AlertType,
  type ModuleAlarmsBase,
} from '../../api/settingsModuleAlarms'
import { toast } from '../../ui/notify'
import { Spinner } from '../../ui/Spinner'

/**
 * Alarmele proprii ale unui modul-sora: `securitee_settings/alarms/protection.php`
 * si `printeeng_settings/alarms/index.php` (lista de categorii in stanga,
 * `#alarms-nav-container` in dreapta), cu `AlarmsView`/`AlarmsList` din
 * `public/js/settings/securitee.js` (579-1014) si formularele
 * `alarms/create_form.php` / `edit_form.php`, element cu element.
 */
export function ModuleAlarmsEditor({ base, categories }: { base: ModuleAlarmsBase; categories: AlarmCategory[] }) {
  const first = categories.find((c) => c.alert_types.length > 0)?.alert_types[0]?.ID ?? 0
  const [type, setType] = useState(first)
  useEffect(() => {
    if (type === 0 && first > 0) setType(first)
  }, [first, type])

  return (
    <div id="default-alarms-content" className="row">
      <div className="col-md-2">
        <div className="well" style={{ width: 'auto', borderRadius: 4, backgroundColor: '#f5f5f5' }}>
          <ul id="default-alarms-nav" className="nav nav-pills nav-stacked nav-list">
            {categories.map((category) => (
              <CategoryNav key={category.ID} category={category} current={type} onPick={setType} />
            ))}
          </ul>
        </div>
      </div>
      <div className="col-md-10" id="alarms-nav-container">
        {type > 0 && <AlarmsView key={type} base={base} type={type} />}
      </div>
    </div>
  )
}

function CategoryNav({ category, current, onPick }: { category: AlarmCategory; current: number; onPick: (t: number) => void }) {
  return (
    <>
      <li className="nav-header" style={{ fontWeight: 'bold' }}>
        {category.category_name}
      </li>
      {category.alert_types.map((alertType) => (
        <li key={alertType.ID} className={alertType.ID === current ? 'nav-tab active' : 'nav-tab'}>
          <a
            data-alerttype={alertType.ID}
            href="#"
            onClick={(e) => {
              e.preventDefault()
              onPick(alertType.ID)
            }}
          >
            {alertType.type_name}
          </a>
        </li>
      ))}
    </>
  )
}

type Mode = { kind: 'list' } | { kind: 'create' } | { kind: 'edit'; id: number }

function AlarmsView({ base, type }: { base: ModuleAlarmsBase; type: number }) {
  const client = useQueryClient()
  const list = useQuery(moduleAlarmListQuery(base, type))
  const [mode, setMode] = useState<Mode>({ kind: 'list' })
  const reload = () => {
    setMode({ kind: 'list' })
    void client.invalidateQueries({ queryKey: ['settings', base, 'alarms', 'type', type] })
  }

  if (list.error instanceof ApiError && list.error.status === 403) {
    return (
      <div className="alert alert-danger">
        <i className="fas fa-exclamation-circle" aria-hidden="true"></i> You do not have access to this action.
      </div>
    )
  }
  if (!list.data) return <Spinner />
  const alertType = list.data.alertType

  if (mode.kind === 'create' && alertType) {
    return <AlarmForm base={base} alertType={alertType} onDone={reload} onCancel={() => setMode({ kind: 'list' })} />
  }
  if (mode.kind === 'edit') {
    return <EditAlarm base={base} id={mode.id} onDone={reload} onCancel={() => setMode({ kind: 'list' })} />
  }

  return (
    <>
      <div className="alarms-header">
        <h4>
          {alertType?.type_name ?? ''}
          <button
            className="btn btn-sm btn-success pull-right"
            id="create-alarm-btn"
            data-alert={type}
            onClick={() => setMode({ kind: 'create' })}
          >
            <i className="fas fa-plus" aria-hidden="true"></i> New alarm
          </button>
        </h4>
        <span className="help-block">{alertType?.alert_type_description ?? ''}</span>
      </div>
      <hr />
      <div className="alarms-container">
        {list.data.default ? (
          <>
            <div className="alert alert-info">
              <i className="fas fa-info-circle" aria-hidden="true"></i> No settings for this alarm type. You can load default settings or create a new alarm
            </div>
            <button
              className="btn btn-sm btn-primary"
              id="load-default-settings"
              data-type={type}
              onClick={() => {
                void moduleAlarmLoadDefaults(base, type).then(reload)
              }}
            >
              Load default settings
            </button>
          </>
        ) : list.data.alarms.length === 0 ? (
          <div className="alert alert-info">
            <i className="fas fa-info-circle" aria-hidden="true"></i> No alarms
          </div>
        ) : alertType?.dinamic === 1 ? (
          <DinamicAlarms base={base} alarms={list.data.alarms} alertType={alertType} onEdit={(id) => setMode({ kind: 'edit', id })} onChanged={reload} />
        ) : (
          list.data.alarms.map((alarm) => (
            <SingleAlarm key={num(alarm, 'ID')} base={base} alarm={alarm} onEdit={(id) => setMode({ kind: 'edit', id })} onChanged={reload} />
          ))
        )}
      </div>
    </>
  )
}

function num(row: Record<string, unknown>, key: string): number {
  return Number(row[key] ?? 0)
}

function alarmFlag(flag: unknown) {
  switch (String(flag)) {
    case '0':
      return <label className="label label-primary"><i className="far fa-bell"></i> info</label>
    case '1':
      return <label className="label label-success"><i className="far fa-bell"></i> minor</label>
    case '2':
      return <label className="label label-warning"><i className="far fa-bell"></i> major</label>
    case '3':
      return <label className="label label-danger"><i className="far fa-bell"></i> critical</label>
    default:
      return null
  }
}

function sendEmail(alarm: AlarmRow) {
  return num(alarm, 'actions') === 1 ? (
    <i className="fas fa-check-circle color-success font-16"></i>
  ) : (
    <i className="fas fa-times-circle color-danger font-16"></i>
  )
}

function useRowActions(base: ModuleAlarmsBase, onChanged: () => void) {
  const onStatus = (id: number, checked: boolean) => {
    void moduleAlarmStatus(base, id, checked ? 1 : 0).then(() => toast('Alarm status changed', 'Alarm settings', 'success'))
  }
  const onRemove = (id: number) => {
    if (!confirm('Are you sure?\nData will be lost.')) return
    void moduleAlarmRemove(base, id)
      .then(() => {
        toast('Alarm settings deleted.', 'Success', 'success')
        onChanged()
      })
      .catch(() => toast('An error ocurred. Please try again later.', 'Error deleting', 'error'))
  }
  return { onStatus, onRemove }
}

function Actions({ alarm, onEdit, onRemove }: { alarm: AlarmRow; onEdit: (id: number) => void; onRemove: (id: number) => void }) {
  const id = num(alarm, 'ID')
  return (
    <>
      <button className="btn btn-primary btn-sm edit-alarm" data-alarm={id} data-alarmtype={num(alarm, 'alert_type')} onClick={() => onEdit(id)}>
        Edit
      </button>{' '}
      <button className="btn btn-danger btn-sm remove-alarm" data-alarm={id} onClick={() => onRemove(id)}>
        Delete
      </button>
    </>
  )
}

function SingleAlarm({ base, alarm, onEdit, onChanged }: { base: ModuleAlarmsBase; alarm: AlarmRow; onEdit: (id: number) => void; onChanged: () => void }) {
  const { onStatus, onRemove } = useRowActions(base, onChanged)
  const id = num(alarm, 'ID')
  return (
    <div className={'row alarm_settings_' + id}>
      <div className="col-md-3">
        <label style={{ display: 'block' }}>Activate</label>
        <input type="checkbox" className="activate-alarm" data-alarm={id} defaultChecked={num(alarm, 'status') === 1} onChange={(e) => onStatus(id, e.target.checked)} />
      </div>
      <div className="col-md-3">
        <label style={{ display: 'block' }}>Severity</label>
        {alarmFlag(alarm.flag)}
      </div>
      <div className="col-md-3">
        <label style={{ display: 'block' }}>Value</label>
        {String(alarm.from_value ?? '')}
      </div>
      <div className="col-md-2">
        <label style={{ display: 'block' }}>Send email</label>
        {sendEmail(alarm)}
      </div>
      <div className="col-md-1">
        <label style={{ display: 'block' }}>Actions</label>
        <Actions alarm={alarm} onEdit={onEdit} onRemove={onRemove} />
      </div>
    </div>
  )
}

function DinamicAlarms({
  base,
  alarms,
  alertType,
  onEdit,
  onChanged,
}: {
  base: ModuleAlarmsBase
  alarms: AlarmRow[]
  alertType: AlertType
  onEdit: (id: number) => void
  onChanged: () => void
}) {
  const { onStatus, onRemove } = useRowActions(base, onChanged)
  return (
    <table className="table table-hover" width="100%">
      <thead style={{ backgroundColor: '#FFF' }}>
        <tr>
          <th>Activate</th>
          <th>Severity</th>
          <th>Thresholds</th>
          <th>Send email</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody id="alarms-details">
        {alarms.map((alarm) => {
          const id = num(alarm, 'ID')
          return (
            <tr key={id} className={'alarm_settings_' + id}>
              <td style={{ padding: 10, width: 30 }}>
                <input type="checkbox" className="activate-alarm" data-alarm={id} defaultChecked={num(alarm, 'status') === 1} onChange={(e) => onStatus(id, e.target.checked)} />
              </td>
              <td style={{ padding: 10, textAlign: 'center' }}>{alarmFlag(alarm.flag)}</td>
              <td style={{ padding: 10, textAlign: 'center' }}>
                MIN : <label className="label label-default">{String(alarm.from_value ?? '')}{alertType.sign ?? ''}</label> - MAX :{' '}
                <label className="label label-default">{String(alarm.to_value ?? '')}{alertType.sign ?? ''}</label>
              </td>
              <td style={{ padding: 10, textAlign: 'center' }}>{sendEmail(alarm)}</td>
              <td style={{ padding: 10, width: 125 }}>
                <Actions alarm={alarm} onEdit={onEdit} onRemove={onRemove} />
              </td>
            </tr>
          )
        })}
      </tbody>
    </table>
  )
}

function EditAlarm({ base, id, onDone, onCancel }: { base: ModuleAlarmsBase; id: number; onDone: () => void; onCancel: () => void }) {
  const [form, setForm] = useState<Awaited<ReturnType<typeof moduleAlarmEditForm>> | null>(null)
  useEffect(() => {
    void moduleAlarmEditForm(base, id).then(setForm)
  }, [base, id])
  if (!form || !form.alarmType) return <Spinner />
  return <AlarmForm base={base} alertType={form.alarmType} alarm={form.alarm} notifiers={form.notifiers.map((n) => n.address)} onDone={onDone} onCancel={onCancel} />
}

/** `create_form.php` / `edit_form.php`: acelasi formular, cu sau fara `alarm-id`. */
function AlarmForm({
  base,
  alertType,
  alarm,
  notifiers = [],
  onDone,
  onCancel,
}: {
  base: ModuleAlarmsBase
  alertType: AlertType
  alarm?: AlarmRow
  notifiers?: string[]
  onDone: () => void
  onCancel: () => void
}) {
  const editing = alarm !== undefined
  const [severity, setSeverity] = useState(String(alarm?.flag ?? '0'))
  const [min, setMin] = useState(String(alarm?.from_value ?? '0'))
  const [max, setMax] = useState(String(alarm?.to_value ?? '0'))
  const [text, setText] = useState(String(alarm?.from_value ?? '0'))
  const [activate, setActivate] = useState(num(alarm ?? {}, 'status') === 1)
  const [email, setEmail] = useState(num(alarm ?? {}, 'actions') === 1)
  const [emails, setEmails] = useState<string[]>(notifiers.length ? notifiers : [''])
  const [errors, setErrors] = useState<string[]>([])
  const [busy, setBusy] = useState(false)
  const dinamic = alertType.dinamic === 1

  const validate = async (): Promise<string[]> => {
    if (!dinamic) return text.trim() === '' ? ['Field is required'] : []
    const out: string[] = []
    if (min.trim() === '' || max.trim() === '') out.push('Field is required')
    if (Number.isNaN(Number(min)) || Number.isNaN(Number(max))) out.push('Field value must be a number')
    if (Number(min) >= Number(max)) out.push('Field value must be lower than Min threshold')
    if (out.length) return out
    const check = await moduleAlarmCheckValue(base, { alarmType: alertType.ID, alarmId: num(alarm ?? {}, 'ID'), minThreshold: min })
    const check2 = await moduleAlarmCheckValue(base, { alarmType: alertType.ID, alarmId: num(alarm ?? {}, 'ID'), maxThreshold: max })
    if (check.valid === 'false' || check2.valid === 'false') {
      out.push('There is a similar value on an existing alert. To create a new alarm please use a different range of values.')
    }
    return out
  }

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    if (busy) return
    setBusy(true)
    try {
      const problems = await validate()
      setErrors(problems)
      if (problems.length) return
      const fields: Record<string, PostValue> = {
        'alarm-type': alertType.ID,
        'alarm-severity': severity,
        notifiers: emails.filter((x) => x.trim() !== '').map((x) => ({ email: x.trim() })),
      }
      if (dinamic) {
        fields.minThreshold = min
        fields.maxThreshold = max
      } else {
        fields.textThreshold = text
      }
      if (activate) fields.activate = 'on'
      if (email) fields.send_email = 'on'
      if (editing) {
        fields['alarm-id'] = num(alarm, 'ID')
        await moduleAlarmUpdate(base, fields)
        toast('Alarm settings updated', 'Alarm settings', 'success')
      } else {
        await moduleAlarmCreate(base, fields)
        toast('New alarm settings created', 'Alarm settings', 'success')
      }
      onDone()
    } catch (err) {
      setErrors(errorMessages(err))
      toast('Something went wrong', 'Alarm settings', 'error')
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <h4>{editing ? 'Edit alarm' : 'Create alarm'}</h4>
      <br />
      <form className="repeater" id={editing ? 'edit-default-alarm-form' : 'create-alarm-form'} method="post" onSubmit={submit}>
        <div className="form-group">
          <label>Severity</label>
          <div className="row">
            <div className="col-md-6">
              <input type="hidden" name="alarm-type" value={alertType.ID} />
              {editing && <input type="hidden" name="alarm-id" value={num(alarm, 'ID')} />}
              <select className="form-control" name="alarm-severity" value={severity} onChange={(e) => setSeverity(e.target.value)}>
                <option value="0">Info</option>
                <option value="1">Minor</option>
                <option value="2">Major</option>
                <option value="3">Critical</option>
              </select>
            </div>
          </div>
        </div>
        <div className="form-group">
          <label className="control-label">Thresholds</label>
          <div className="row">
            {dinamic ? (
              <>
                <div className="col-md-3">
                  <div className="input-group">
                    <span className="input-group-addon">Min</span>
                    <input className="form-control" name="minThreshold" type="number" value={min} onChange={(e) => setMin(e.target.value)} required />
                    <span className="input-group-addon">{alertType.sign ?? ''}</span>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="input-group">
                    <span className="input-group-addon">Max</span>
                    <input className="form-control" name="maxThreshold" type="number" value={max} onChange={(e) => setMax(e.target.value)} required />
                    <span className="input-group-addon">{alertType.sign ?? ''}</span>
                  </div>
                </div>
              </>
            ) : (
              <div className="col-md-3">
                <div className="input-group">
                  <span className="input-group-addon">Value</span>
                  <input className="form-control" name="textThreshold" type="text" value={text} onChange={(e) => setText(e.target.value)} required />
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="form-group">
          <div className="row">
            <div className="col-md-6">
              <label>
                <input type="checkbox" className="icheck-alarm" name="activate" value="on" checked={activate} onChange={(e) => setActivate(e.target.checked)} /> Activate
              </label>
            </div>
          </div>
        </div>
        <div className="form-group">
          <div className="row">
            <div className="col-md-6">
              <label>
                <input type="checkbox" className="icheck-alarm" name="send_email" value="on" checked={email} onChange={(e) => setEmail(e.target.checked)} /> Send email to admin
              </label>
            </div>
          </div>
        </div>
        <div className="form-group">
          <div className="row">
            <div className="col-md-3" data-repeater-list="notifiers" id="repeater_notifiers">
              <label>Emails</label>
              {emails.map((value, i) => (
                <div className="input-group" data-repeater-item key={i}>
                  <input
                    className="form-control input-sm"
                    type="email"
                    name="email"
                    value={value}
                    onChange={(e) => setEmails(emails.map((x, j) => (j === i ? e.target.value : x)))}
                  />
                  <span className="input-group-btn">
                    <button data-repeater-delete className="btn btn-sm btn-default" type="button" onClick={() => setEmails(emails.filter((_, j) => j !== i))}>
                      <i className="fas fa-times"></i>
                    </button>
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="form-group" style={{ marginBottom: 5 }}>
          <div className="row">
            <div className="col-md-2">
              <button type="button" data-repeater-create className="btn btn-xs btn-primary" onClick={() => setEmails([...emails, ''])}>
                Add+
              </button>
            </div>
          </div>
        </div>
        <div id="form-errors">
          {errors.map((message) => (
            <div className="alert alert-danger" key={message}>
              {message}
            </div>
          ))}
        </div>
        <button type="submit" className="btn btn-primary" disabled={busy}>
          Save
        </button>{' '}
        <button type="button" className="btn btn-default" id={editing ? 'cancel-edit-form' : 'cancel-create-form'} data-type={alertType.ID} onClick={onCancel}>
          Cancel
        </button>
      </form>
    </>
  )
}
